// ---------------------------------------------------------------------------
// The host's controls for one player. Lifted out of `LobbyScreen.tsx`; see the
// note on the component for why it is not a `<details>`, and for how it stays
// on a phone screen.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { swallowNextClick } from '../../../lib/swallowNextClick.js';
import type { ReactElement, ReactNode } from 'react';
import type { BotDifficulty, ClientMessage, PublicPlayer, Team } from '@countdown/shared';
import { Crown, StopSlider } from '../../../components/primitives/index.js';
import {
  BanIcon,
  CheckIcon,
  CircleIcon,
  EyeIcon,
  Icon,
  KickIcon,
  MicIcon,
  MoreIcon,
  MuteIcon,
  TrashIcon,
  UserIcon,
} from '../../../components/primitives/Icon.js';
import { useIsDesktop } from '../../../hooks/useMediaQuery.js';
import { useVisualViewport } from '../../../hooks/useVisualViewport.js';
import { formatSeatCost, seatCostLabel, seatCostPerGame } from '../../../lib/format.js';
import type { LlmModelOption } from '../../../lib/api.js';
import { useActionBarHeight } from './actionBar.js';
import { SectionLabel } from './fields.js';

export const BOT_DIFFICULTIES: readonly BotDifficulty[] = [
  'easy',
  'medium',
  'hard',
  'expert',
  'master',
];
export const DIFFICULTY_LABEL: Record<BotDifficulty, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  expert: 'Expert',
  master: 'Master',
};

/**
 * One sentence about reasoning, shared by this menu and the lobby's add panel.
 *
 * Off by default and worth leaving off. Thinking bills as output, so it costs
 * more per round for an answer that is one word either way.
 *
 * **It used to promise "about ten times as much", and that was measured wrong
 * on 2026-08-03.** Ten times is what the toggle is worth on a model that
 * honours it — Haiku and Flash Lite emit literally zero reasoning tokens with
 * it off. Six of the ten models in the catalogue emit 95-100% of their output
 * as reasoning tokens WHATEVER this says, because `reasoning: { exclude: true }`
 * stops the text coming back and does not stop the thinking or the billing. A
 * host who reads a firm number here and turns it off is being told they have
 * saved something they may not have, so the sentence names the doubt instead.
 */
export const REASONING_TITLE =
  'Ask the model to think first. Costs more, and some models think either way.';

/** Breathing room between the menu and the edge of what is on screen. */
const MENU_MARGIN = 8;

/**
 * Below this there is not enough room to open downward at all, so the menu
 * flips above the trigger instead. Roughly four rows and a rule: less than that
 * and a host is scrolling a menu to reach its second item.
 */
const MENU_MIN_HEIGHT = 200;

/**
 * How much of the visible height a bottom sheet may take.
 *
 * Enough that the model catalog is a list rather than a porthole, and short
 * enough that the row it was opened from is still on screen above it — which is
 * the whole of what a sheet has instead of an anchor.
 */
const SHEET_SHARE = 0.72;

/** Where the menu opened and how tall it may be, both in CSS pixels. */
interface Placement {
  above: boolean;
  maxHeight: number;
}

/**
 * What this server will let the host seat, and why not when it will not.
 *
 * Null means the server has AI opponents switched off, so there is no option to
 * offer at all. A non-null `blockedReason` means there is one and this host
 * cannot take it yet — signed out, or the month's budget is spent — and it is
 * shown greyed with the reason under it rather than hidden.
 */
export interface AiSeating {
  defaultModel: string;
  blockedReason: string | null;
}

/**
 * Which half of this menu a caller wants.
 *
 * `lobby` is everything: the opponent panel, the model catalog, the team moves
 * and the four powers over a person. `moderation` is those four powers and
 * nothing else, and it is what the chat panel's roster opens mid-game.
 *
 * **The split is the engine's, not a preference.** `set-bot-difficulty`,
 * `set-bot-model`, `set-bot-kind` and `assign-team` all refuse outright once the
 * phase is not `lobby` — read `assignTeam`, which says mid-game team formation
 * would make best-of scoring incoherent, and `setBotModel`, which is
 * lobby-gated for the same reason its two neighbours are. Offering any of them
 * on a live game would be offering a control that answers with an error toast.
 * Kick, ban, mute and transfer are gated on being the host and on nothing else,
 * in the engine and in `socket.ts` alike, so they are the four that work in
 * every phase and they are exactly the four this scope keeps.
 */
export type HostMenuScope = 'lobby' | 'moderation';

/**
 * The host's controls for one player.
 *
 * **This was a native `<details>` and that was the wrong trade.** The comment
 * defending it said a disclosure needs no outside-click handling and gets the
 * keyboard for free. What it actually got was a menu that closes on nothing but
 * its own summary: a host working down a row of bots opened one menu per bot
 * and left all of them stacked over each other. Neither half of the trade
 * survives contact either — exclusivity between sibling disclosures needs the
 * `name` attribute, which is not in every browser this ships to, and an
 * outside press has to be a document listener whichever element holds the
 * state. So there was nothing left to buy.
 *
 * Which menu is open is therefore one piece of state up in the screen, keyed by
 * player id. Exclusivity is structural rather than coordinated: one id cannot
 * name two menus, so opening one closes the last without either of them
 * knowing about the other.
 *
 * It stays anchored `absolute` inside its `relative` row. The seat list
 * scrolls, and a menu in a portal or the top layer would have to be positioned
 * against the viewport, which means it slides off the row it belongs to the
 * moment the list moves.
 *
 * **ON A PHONE IT IS NOT ANCHORED AT ALL.** Below 900px this is a bottom sheet:
 * a scrim, a panel on the bottom edge of the visible area, dismissed by a press
 * outside it or by Escape. That is Isaac's own suggestion and it is the right
 * one, because a sheet needs no trigger-relative geometry — it removes the
 * entire class of bug below rather than fixing one more instance of it, and it
 * puts a menu that can be five rows and a scrolling model catalog at the end of
 * a thumb instead of hanging off a 44px target near the top of the screen.
 *
 * Above 900px it stays a popover, because a pointer is precise, the window is
 * tall, and a sheet on a desktop is a modal interruption for "mute this player".
 * The breakpoint is `useIsDesktop()`, which is the one this room already
 * switches its chat at.
 *
 * **Anchored is not the same as ignoring the screen, and it used to be.** The
 * menu always opened downward at whatever height its contents came to, so a bot
 * near the bottom of a phone opened a menu that was entirely below the fold —
 * every item of it off screen, with nothing to say so. `place()` measures the
 * trigger against the visible band and answers two questions: open down or flip
 * up, and how tall the menu may be before it has to scroll. The band comes from
 * `useVisualViewport`, so a keyboard that is up counts as screen that is gone;
 * iOS scrolls the visual viewport rather than resizing the layout one, which is
 * why `window.innerHeight` alone is the wrong number (CLAUDE.md).
 *
 * **The band was still wrong, and this is the bug that was reported after the
 * flip shipped.** The lobby's Start / Ready bar is `sticky bottom-0` over the
 * seat list, and nothing in the arithmetic knew about it: `bottomInset` is the
 * keyboard and only the keyboard, so the maths believed the bottom hundred-odd
 * pixels of the screen were usable when a bar was sitting on them. Measured on
 * a 375px phone with seven bots in the room, that bar is 105px tall and a row
 * two thirds of the way down reported 247px of room under it where it had 142
 * — over the flip's 200px threshold, so no flip, and 101px of the menu opened
 * behind the bar. The rows that most needed the flip were exactly the rows the
 * overstatement pushed back over the line. The bottom of the band is now
 * `innerHeight - max(bottomInset, actionBar)`: two different things can cover
 * the bottom of the screen and only the deeper of them decides where the band
 * ends. See `actionBar.ts` for why the bar reports its own height rather than
 * having one written down.
 *
 * It is measured in the press that opens the menu and again on scroll and
 * resize, never in an effect body: setting state synchronously inside an effect
 * is a lint error here, and the press already knows where the trigger is.
 *
 * Ban is two-step, and its first press is one of the two things in here that
 * leave the menu open. It is room-scoped and keyed on device and IP for a
 * guest, so it is not something to fire on one stray tap next to Kick.
 *
 * **It is no longer only the lobby's, and that is what `scope` is for.** The
 * chat panel's roster opens this same component mid-game with
 * `scope="moderation"`, which is the four powers over a person and nothing else.
 * A second implementation was the obvious alternative and was the wrong one:
 * everything hard about this component is in the dismissal rules and the
 * placement arithmetic below — the `pointerdown`-only rule that lets a slider
 * drag survive, the `swallowNextClick` on a scrim press, the band that knows
 * about a keyboard and a sticky bar — and none of that is about which items are
 * in the list. A copy would have started correct and drifted, and the drift
 * would show up as a menu that dismisses itself on a phone during a live round.
 *
 * **A bot's choices are one panel, not two.** It used to show a difficulty
 * list for a scripted bot and a model list for an AI one, and there was no way
 * from either to the other: a host who wanted the bot in seat three to be a
 * language model had to remove it and add one, which cost the seat its name,
 * its colour and its place in the running order. A difficulty slider and an AI
 * item is the same pair of questions the lobby's add panel asks, and crossing
 * between them converts the bot in place.
 *
 * **AI is the press that leaves the menu open, and the difficulty behind it
 * becomes a way back rather than a reading.** Choosing AI is not the end of a
 * decision, it is the start of one — the model is the next question and it is
 * asked in this same menu — so closing on it meant reopening it immediately.
 * Once a bot is an AI the slider is still there, because it is the way back to a
 * scripted bot, but it is labelled as the swap it performs: an AI has no
 * difficulty, so a control claiming to show one would be inventing a fact.
 */
export function HostRowMenu({
  send,
  player,
  teams,
  open,
  onOpenChange,
  llmModels = [],
  rounds = 0,
  aiSeating = null,
  scope = 'lobby',
}: {
  send: (message: ClientMessage) => void;
  player: PublicPlayer;
  teams: Team[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The models this server offers. Empty when AI opponents are switched off.
   *
   * Optional because the three props below it are all about seating an opponent,
   * and the moderation scope has no opponent panel to put them in — a caller
   * there would be passing an empty array, a zero and a null purely to satisfy
   * the shape.
   */
  llmModels?: readonly LlmModelOption[];
  /**
   * How many rounds this room is set to play, so a seat is priced for the game
   * about to happen rather than for some notional average. Changing the round
   * plan from Short to Full triples every figure in this list, which is true and
   * is the sort of thing a host should be able to see themselves do.
   */
  rounds?: number;
  /** Null when this server has AI opponents switched off: no AI choice at all. */
  aiSeating?: AiSeating | null;
  /** Defaults to the lobby's full menu, so every existing caller is unchanged. */
  scope?: HostMenuScope;
}): ReactElement {
  const moderationOnly = scope === 'moderation';
  const [confirmBan, setConfirmBan] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // The menu itself, which on a phone is portalled clean out of `rootRef`. The
  // outside-press listener has to know about both or every press inside the
  // sheet would read as a press outside the menu and close it.
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  // The darkened area behind the sheet. Only the phone variant has one, and
  // whether the press landed on it is what decides `swallowNextClick` below.
  const scrimRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Outside the close effect on purpose: closing is what tears that effect
  // down, and a timer cleared by its own cause never runs.
  const restoreRef = useRef(0);
  useEffect(() => () => window.clearTimeout(restoreRef.current), []);

  const viewport = useVisualViewport();
  const actionBar = useActionBarHeight();
  const isDesktop = useIsDesktop();

  /** The visible height, whatever this browser is willing to say about it. */
  const visibleHeight = viewport.height > 0 ? viewport.height : window.innerHeight;

  /**
   * Where this menu fits right now. Popover only: a sheet has no geometry to
   * work out, which is the reason it is a sheet.
   *
   * Everything here is in CLIENT coordinates, because `getBoundingClientRect`
   * is, and that decides which of the viewport hook's two insets belongs in it.
   *
   * **`bottomInset` is the right one here and `coveredHeight` is not**, which is
   * the opposite of the choice the sheet below makes, so it is worth writing the
   * algebra down rather than leaving the next reader to swap them:
   *
   *     innerHeight - bottomInset
   *       = innerHeight - (innerHeight - visualViewport.height - offsetTop)
   *       = offsetTop + visualViewport.height
   *
   * — which is exactly where the bottom of the visible region falls inside the
   * layout viewport, and therefore exactly the number a client rect compares
   * against. Both `innerHeight` terms cancel, so nothing scroll-dependent
   * survives the subtraction: this is not the double-correction that the note on
   * `coveredHeight` warns about, because that one is about an element the
   * browser has already moved before it is painted, and a menu measured against
   * a client rect is not one. Substituting `coveredHeight` would drop
   * `offsetTop` and put the band in the wrong place by however far iOS has slid
   * the visual viewport.
   *
   * The other end of the band is the thing that was missing. Two different
   * things can cover the bottom of the screen and they do not add up — a
   * keyboard and a bar pinned to the bottom eat the same pixels from the same
   * edge — so the band ends at whichever of them reaches higher. The top is
   * worked out from the visible region alone and never from the bar, or a bar
   * would make the band look shorter at both ends.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (trigger === null) return;
    const rect = trigger.getBoundingClientRect();
    const height = viewport.height > 0 ? viewport.height : window.innerHeight;
    const visibleBottom = window.innerHeight - viewport.bottomInset;
    const bandTop = Math.max(0, visibleBottom - height);
    const bandBottom = Math.min(visibleBottom, window.innerHeight - actionBar);

    const below = bandBottom - rect.bottom - MENU_MARGIN;
    const above = rect.top - bandTop - MENU_MARGIN;
    // Down unless down is unusable. A menu that flips up whenever up is roomier
    // would jump sides halfway down a list for no reason a host can see.
    const flip = below < MENU_MIN_HEIGHT && above > below;
    setPlacement({
      above: flip,
      // Never below the minimum: on a screen with room for neither, a menu that
      // scrolls its own contents is the only thing left, and it is better than
      // one squeezed to nothing.
      maxHeight: Math.max(MENU_MIN_HEIGHT, Math.round(flip ? above : below)),
    });
  }, [actionBar, viewport.bottomInset, viewport.height]);

  // One geometry for every row, so the menu is the same size whether a row is
  // showing a tick, a bin or a crown. Only the colour differs below.
  const row =
    'flex min-h-11 w-full items-center gap-2.5 rounded-chip px-3 text-left text-[13px] font-semibold transition-colors duration-150 ease-out';
  const item = `${row} text-ink hover:bg-raised`;
  const danger = `${row} text-alert hover:bg-alert/15`;
  /** Holds the destructive tail off the choices above it. */
  const divider = <div aria-hidden="true" className="mx-2 my-1 h-px bg-line" />;

  // Closing hands focus back to the three dots. Without it, a keyboard user who
  // presses Escape is dropped at the top of the document, a scroll away from
  // the row they were working on.
  const close = useCallback(() => {
    onOpenChange(false);
    triggerRef.current?.focus();
  }, [onOpenChange]);

  /** Every item but the two that ask a follow-up: send it, and the menu is done. */
  const choose = (message: ClientMessage) => () => {
    send(message);
    close();
  };

  useEffect(() => {
    if (!open) return;

    // `pointerdown`, not `click`: a press that begins outside the menu already
    // means "not this menu", and waiting for the click leaves it open under a
    // finger that has started a scroll. Captured, so an item that stops
    // propagation cannot keep the menu alive.
    //
    // **The difficulty slider lives or dies on this being pointerdown alone.** A
    // drag is one press inside the menu and then a stream of moves that a thumb
    // takes clean outside it before letting go, so this listener hears the
    // press, finds it inside, and never hears the rest. Anything added here that
    // also closes on `pointermove` or `pointerup` — a "dismiss on any press
    // ending outside" rule reads as the same idea — dismisses the menu in the
    // middle of every drag. `LobbyScreen.test.tsx` drags out to the body and
    // releases there, which is the case that would fail.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) === true) return;
      if (surfaceRef.current?.contains(target) === true) return;
      // **A press on the scrim means GET ME OUT and nothing else.** Closing here
      // unmounts the scrim before the finger comes up, so the browser hit-tests
      // the click that follows against whatever is under that point now — which
      // on the lobby is another player's three-dots button, and their menu opens
      // on the way out of this one. Isaac reported exactly that. The press has
      // to stay the dismissal (the slider drag above depends on it), so the
      // click it is about to make is eaten instead. See `swallowNextClick`.
      //
      // Only for the scrim. The desktop popover has no backdrop, so "outside"
      // there is whatever the host was aiming at and pressing it should work.
      if (scrimRef.current?.contains(target) === true) swallowNextClick();
      onOpenChange(false);
      // Focus goes back a task later rather than here. The browser's own press
      // handling moves focus to whatever was pressed — to the body, when that
      // is nothing focusable — and it does that *after* this listener runs, so
      // restoring now would only be overwritten. And it goes back only if
      // nothing else claimed it: a host who pressed another control should be
      // left in that control, not yanked back to a menu they just dismissed.
      restoreRef.current = window.setTimeout(() => {
        if (document.activeElement === document.body) triggerRef.current?.focus();
      }, 0);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    // Captured, because the list this row sits in is the thing that scrolls and
    // a scroll on a container does not bubble to the window. A sheet is pinned
    // to the screen rather than to a row, so it subscribes to neither.
    if (isDesktop) {
      window.addEventListener('scroll', place, true);
      window.addEventListener('resize', place);
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, isDesktop, onOpenChange, place]);

  /**
   * The difficulty, for a bot that has one.
   *
   * **A slider rather than five rows.** Three difficulties fitted a menu as
   * three items with a tick on one; five is half the height of a phone spent on
   * a single question, and it pushed the model list — which is what this menu is
   * mostly for — off the bottom. The same control is in the lobby's add panel,
   * so the choice looks the same wherever it is asked.
   *
   * **It does not close the menu, and every other choice in here does.** A
   * slider commits once per gesture, and on a keyboard a gesture is one arrow
   * key: closing on the first commit would mean a host stepping from Easy to
   * Master reopening the menu four times. It is a control the host settles, not
   * a command they issue, so it stays put until they dismiss it themselves.
   */
  const difficultySlider = (
    <div className="px-3 pb-1">
      <StopSlider
        label="Difficulty"
        showLabel
        stops={BOT_DIFFICULTIES}
        labelFor={(difficulty) => DIFFICULTY_LABEL[difficulty]}
        // A bot the server has not told us the difficulty of sits at the same
        // default `add-bot` uses, rather than off the end of the track.
        value={player.botDifficulty ?? 'medium'}
        // A scripted bot changing difficulty is not a conversion and goes on
        // sending `set-bot-difficulty`, which is the action it always sent.
        // `set-bot-kind` is for crossing the line, which keeps the one action
        // that can start a spend to the one press that means it.
        onChange={(difficulty) =>
          send({ type: 'set-bot-difficulty', playerId: player.id, difficulty })
        }
      />
    </div>
  );

  /**
   * The same control on a bot that is currently an AI, where moving it is a
   * conversion rather than a selection.
   *
   * An AI has no difficulty, so nothing here is a reading of what the seat is
   * set to — the label says what the control does and the readout says where it
   * would land. That is why it sends `set-bot-kind`: crossing the line is the
   * action that starts a bot being scripted again, and it is the same one the
   * row of buttons here used to send.
   */
  const convertToScriptedSlider = (
    <div className="px-3 pb-1">
      <StopSlider
        // Short because the header puts the readout beside it, and the popover
        // is 224px wide: `Swap for a scripted bot` wrapped to two lines there.
        // `bot` is already this menu's word for the scripted one — the item
        // below it says `Remove AI` or `Remove bot` on the same distinction.
        label="Swap for a bot"
        showLabel
        stops={BOT_DIFFICULTIES}
        labelFor={(difficulty) => DIFFICULTY_LABEL[difficulty]}
        value={player.botDifficulty ?? 'medium'}
        onChange={(difficulty) =>
          send({ type: 'set-bot-kind', playerId: player.id, botKind: 'heuristic', difficulty })
        }
      />
    </div>
  );

  /**
   * Every item, once, for both shapes.
   *
   * The sheet and the popover differ in where they hang and how they are
   * dismissed and in nothing else. Two copies of this list is how the phone and
   * the desktop end up offering different powers over the same player.
   */
  const items: ReactNode = (
    <>
      {player.isBot && !moderationOnly ? (
        <>
          <div className="px-3 pt-1 pb-1.5">
            <SectionLabel>opponent</SectionLabel>
          </div>
          {player.botKind === 'llm' ? convertToScriptedSlider : difficultySlider}
          {/* Nothing at all when this server has AI switched off. Present
              but unpressable, with the reason under it, when the host is
              signed out or the month's budget is spent — the same trade the
              add panel makes, for the same reason. */}
          {aiSeating === null ? null : (
            <>
              <button
                type="button"
                aria-pressed={player.botKind === 'llm'}
                disabled={aiSeating.blockedReason !== null}
                onClick={() => {
                  send({
                    type: 'set-bot-kind',
                    playerId: player.id,
                    botKind: 'llm',
                    // The model it already had if it has been AI before,
                    // otherwise this server's default — the same fallback
                    // `add-bot` uses, and the server applies it again anyway.
                    model: player.botModel ?? aiSeating.defaultModel,
                    reasoning: player.botReasoning,
                  });
                  // Deliberately still open: the model is the next question
                  // and it is asked right here, so closing would only mean
                  // opening this menu again.
                }}
                className={`${item} ${player.botKind === 'llm' ? 'bg-raised' : ''} ${
                  aiSeating.blockedReason === null ? '' : 'cursor-not-allowed opacity-60'
                }`}
              >
                <Icon
                  icon={player.botKind === 'llm' ? CheckIcon : CircleIcon}
                  size={16}
                  className={player.botKind === 'llm' ? '' : 'text-ink-dim'}
                />
                AI
              </button>
              {aiSeating.blockedReason === null ? null : (
                <p className="px-3 pb-1 text-[12px] text-ink-muted">
                  {aiSeating.blockedReason}
                </p>
              )}
            </>
          )}
          {player.botKind === 'llm' ? (
            <>
              {/* The rule under the label is the scroll cue. The catalog is
                  longer than a menu should be tall, and a list that simply
                  ran out at the bottom edge of the menu looked like a list
                  that had ended. A bounded box under a rule reads as a box
                  with more in it — and `scrollbar-color` puts a real, thin
                  bar down the side of it wherever the browser allows one,
                  which macOS's overlay scrollbars otherwise hide until the
                  moment somebody already scrolling looks for it. */}
              <div className="mt-1 flex items-center gap-2 border-b border-line px-3 pt-1 pb-1.5">
                <SectionLabel>model</SectionLabel>
                <span className="font-mono text-[11px] text-ink-dim tabular">
                  {llmModels.length}
                </span>
                {/* The unit for the price column, stated once, so ten rows do
                    not each repeat "a game". It would fit on the rows — measured
                    at 320px against the longest name in the catalogue — which is
                    not the same as it being worth saying ten times. Hidden from
                    assistive tech because it labels nothing structurally: each
                    price carries its own readable phrase instead. */}
                <span aria-hidden="true" className="ml-auto text-[11px] text-ink-dim">
                  a game
                </span>
              </div>
              {/* **`shrink-0`, and without it this list disappears entirely.**
                  Reported from a phone with teams on: the sheet showed `MODEL
                  14`, a rule under it, and then nothing at all where fourteen
                  models should be.

                  It is a flex item in a column that is itself scrollable, and
                  `overflow-y: auto` removes the automatic minimum size a flex
                  item normally has — that floor only applies while overflow is
                  visible. So the one child in the sheet that was allowed to
                  shrink below its content shrank to zero the moment the sheet
                  had more rows than it had room for, which is exactly what
                  turning teams on does: three more rows at the bottom.

                  **And on a phone it has no scroller of its own now.** The
                  sheet already scrolls, and a 192px porthole inside a scrolling
                  sheet is a trap — a thumb that lands on the catalogue scrolls
                  the catalogue when it meant to scroll the sheet, and
                  `overscroll-contain` is what stops that gesture reaching the
                  thing the player was actually aiming at. The cap stays on the
                  desktop popover, which is anchored to a row and cannot grow
                  past `placement.maxHeight`. */}
              <div
                data-testid="model-list"
                className={`shrink-0 ${
                  isDesktop ? 'max-h-48 overflow-y-auto overscroll-contain' : ''
                }`}
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'var(--c-line-strong) transparent',
                }}
              >
                {llmModels.map((model) => {
                  const on = player.botModel === model.id;
                  // What this seat costs for the game this room is set to play.
                  // The list is ordered by exactly this number server-side, so
                  // the column reads down — which is the point of showing it.
                  // A server too old to send the tokens shows nothing here
                  // rather than a zero.
                  const cost = seatCostPerGame(model, rounds);
                  return (
                    <button
                      key={model.id}
                      type="button"
                      aria-pressed={on}
                      title={model.note}
                      onClick={choose({
                        type: 'set-bot-model',
                        playerId: player.id,
                        model: model.id,
                        reasoning: player.botReasoning,
                      })}
                      className={`${item} w-full ${on ? 'bg-raised' : ''}`}
                    >
                      <Icon
                        icon={on ? CheckIcon : CircleIcon}
                        size={16}
                        className={on ? '' : 'text-ink-dim'}
                      />
                      <span className="truncate">{model.label}</span>
                      {cost !== null && (
                        <span
                          aria-label={seatCostLabel(cost)}
                          className="ml-auto shrink-0 pl-2 text-xs tabular-nums text-ink-dim"
                        >
                          {formatSeatCost(cost)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* `Show its thinking` was here, and it is gone with
                  `REASONING_AVAILABLE`. Native reasoning bills about ten times a
                  plain answer for a game that is one word a round, and it was the
                  most expensive thing a room could switch on. Nothing else about
                  the feature was removed — a game recorded while it existed still
                  shows what its models were thinking. Put the toggle back when
                  the flag goes back, not before: with the flag off the engine
                  stores `botReasoning` as false whatever this sends, so a control
                  here would be a switch that does nothing. */}
            </>
          ) : null}
          {divider}
          <button
            type="button"
            onClick={choose({ type: 'remove-bot', playerId: player.id })}
            className={danger}
          >
            <Icon icon={TrashIcon} size={16} />
            {player.botKind === 'llm' ? 'Remove AI' : 'Remove bot'}
          </button>
        </>
      ) : (
        <>
          {/* **The other half of the lock, and it is the first item for that
              reason.** A lock puts arrivals on the bench, and without a way back
              off it a streamer whose friend turned up thirty seconds late has no
              move except unlocking the room to the whole audience.

              `assign-role` rather than `set-role`, because the subject is not
              the sender: `set-role` is a player choosing where they sit and
              needs no permission, this is the host doing the seating and is
              theirs alone. Lobby only, like `set-role` — which is why it is
              absent from the moderation scope rather than greyed there, since
              the engine refuses it outright once a game is running. Never for a
              bot: a bot on the bench is a seat that answers nothing. */}
          {moderationOnly ? null : (
            <>
              <button
                type="button"
                data-testid="assign-role"
                onClick={choose({
                  type: 'assign-role',
                  playerId: player.id,
                  role: player.role === 'player' ? 'spectator' : 'player',
                })}
                className={item}
              >
                <Icon icon={player.role === 'player' ? EyeIcon : UserIcon} size={16} />
                {player.role === 'player' ? 'Move to the bench' : 'Give them a seat'}
              </button>
              {divider}
            </>
          )}
          <button
            type="button"
            onClick={choose({ type: 'mute-player', playerId: player.id, muted: !player.muted })}
            className={item}
          >
            <Icon icon={player.muted ? MicIcon : MuteIcon} size={16} />
            {player.muted ? 'Let them chat' : 'Mute in chat'}
          </button>
          {/* Never for a bot. `transferHost` refuses one outright — a bot cannot
              host — so the item would be a press that produces an error toast.
              The lobby never reaches here with a bot; the moderation scope can,
              because a bot falls through to this branch when the opponent panel
              is switched off. */}
          {player.isBot ? null : (
            <button
              type="button"
              onClick={choose({ type: 'transfer-host', playerId: player.id })}
              className={item}
            >
              {/* The crown they would get, not a Lucide stand-in for it: it is
                  the same mark the seat rows draw on the host. */}
              <Crown size={16} />
              Make host
            </button>
          )}
          {divider}
          <button
            type="button"
            onClick={choose({ type: 'kick-player', playerId: player.id })}
            className={danger}
          >
            <Icon icon={KickIcon} size={16} />
            Kick
          </button>
          {confirmBan ? (
            <button
              type="button"
              onClick={choose({ type: 'ban-player', playerId: player.id })}
              className={`${danger} bg-alert/15`}
            >
              <Icon icon={BanIcon} size={16} />
              Ban, and keep them out
            </button>
          ) : (
            // The other item that leaves the menu open, because it has not
            // done anything yet.
            <button type="button" onClick={() => setConfirmBan(true)} className={danger}>
              <Icon icon={BanIcon} size={16} />
              Ban
            </button>
          )}
        </>
      )}

      {/* Nobody on the bench is on a team: `assign-team` is about who is playing
          beside whom, and offering it for somebody who is not playing would be
          a press the engine answers with an error. Seat them first — the item
          for it is at the top of this same menu. */}
      {teams.length > 0 && !moderationOnly && player.role === 'player' ? (
        <>
          <div className="px-3 pt-2 pb-1.5">
            <SectionLabel>team</SectionLabel>
          </div>
          {teams.map((team) => {
            const on = player.teamId === team.id;
            return (
              <button
                key={team.id}
                type="button"
                aria-pressed={on}
                onClick={choose({ type: 'assign-team', playerId: player.id, teamId: team.id })}
                className={`${item} ${on ? 'bg-raised' : ''}`}
              >
                <Icon
                  icon={on ? CheckIcon : CircleIcon}
                  size={16}
                  className={on ? '' : 'text-ink-dim'}
                />
                Move to {team.name}
              </button>
            );
          })}
          {player.teamId === null ? null : (
            <button
              type="button"
              onClick={choose({ type: 'assign-team', playerId: player.id, teamId: null })}
              className={item}
            >
              <Icon icon={CircleIcon} size={16} className="text-ink-dim" />
              Play on their own
            </button>
          )}
        </>
      ) : null}
    </>
  );

  /**
   * The phone's shape: a sheet on the bottom edge of the visible area.
   *
   * **Pinned with `coveredHeight`, not with `bottomInset`.** This is a
   * `position: fixed` box, and the note on those two readings in
   * `useVisualViewport.ts` is explicit about which is which — `bottomInset`
   * carries two terms the browser has already applied to a fixed element by the
   * time it is painted, so a sheet held up by it slides up and down the screen
   * as the page scrolls under a keyboard. The popover above uses `bottomInset`
   * for the opposite and equally deliberate reason: it is laid out in the layout
   * viewport and compared against `getBoundingClientRect`.
   *
   * The scrim is not a separate dismissal path. It is simply not inside
   * `surfaceRef`, so the outside-press listener that already existed closes on
   * it, and Escape already worked.
   */
  const sheet = createPortal(
    <div
      ref={scrimRef}
      data-testid="host-menu-scrim"
      className="fade-in fixed inset-0 z-[35] flex flex-col justify-end"
      style={{ background: 'var(--c-scrim)', paddingBottom: viewport.coveredHeight }}
    >
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Host controls for ${player.displayName}`}
        data-testid="host-menu"
        data-menu-variant="sheet"
        style={{ maxHeight: Math.max(MENU_MIN_HEIGHT, Math.round(visibleHeight * SHEET_SHARE)) }}
        className="sheet-up mx-auto flex w-full max-w-[420px] flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-t-frame border-t border-line bg-surface p-2 pb-[max(env(safe-area-inset-bottom),10px)]"
      >
        {/* The grab bar says the sheet is dismissible without spending a row on
            a Cancel button. It is not a control: there is nothing to drag it
            with, and pressing anywhere off the sheet is the gesture. */}
        <div className="flex shrink-0 justify-center pt-1 pb-2">
          <span aria-hidden="true" className="h-1 w-9 rounded-chip bg-line-strong" />
        </div>
        {/* Whose row this is. A popover is attached to the row and says so by
            being there; a sheet is at the other end of the screen. */}
        <div className="shrink-0 px-3 pb-1.5">
          <SectionLabel>{player.displayName}</SectionLabel>
        </div>
        {items}
      </div>
    </div>,
    document.body,
  );

  const popover = (
    <div
      ref={surfaceRef}
      data-testid="host-menu"
      data-menu-variant="popover"
      data-menu-above={placement?.above === true || undefined}
      style={placement === null ? undefined : { maxHeight: placement.maxHeight }}
      className={`absolute right-0 z-10 flex w-56 flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-card border border-line bg-surface p-1.5 ${
        placement?.above === true ? 'bottom-full mb-1' : 'top-full mt-1'
      }`}
    >
      {items}
    </div>
  );

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Host controls for ${player.displayName}`}
        aria-haspopup={isDesktop ? 'true' : 'dialog'}
        aria-expanded={open}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          // Reset on the way in rather than on the way out: a menu that closed
          // mid-ban opens back on Ban, so the second press cannot land on a
          // confirmation the host has already walked away from once.
          setConfirmBan(false);
          // Measured here, in the press, rather than in an effect after the
          // open: the trigger is on screen either way, and a setState inside an
          // effect is a lint error in this repo. A sheet has nothing to measure.
          if (isDesktop) place();
          onOpenChange(true);
        }}
        // 44 square. It was 44 by 36, which is the one tap target on this screen
        // that was under the minimum, and it sits at the right edge of a row on
        // a phone where a thumb arrives at an angle.
        className="grid h-11 w-11 cursor-pointer place-items-center rounded-chip text-ink-dim transition-colors duration-150 ease-out hover:text-ink"
      >
        <Icon icon={MoreIcon} size={16} />
      </button>
      {open ? (isDesktop ? popover : sheet) : null}
    </div>
  );
}
